import axios from 'axios';
import { resolveApiBaseURL } from '../utils/apiUtils';

const readAuthToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const sessionToken = window.sessionStorage.getItem('token');
  if (sessionToken) {
    return sessionToken;
  }

  const legacyToken = window.localStorage.getItem('token');
  if (legacyToken) {
    // Mirror into sessionStorage for tab-scoped reads; do NOT remove from localStorage —
    // AuthContext, ProtectedRoute, and many call sites still read localStorage only.
    window.sessionStorage.setItem('token', legacyToken);
    return legacyToken;
  }

  return null;
};

const api = axios.create({
  baseURL: resolveApiBaseURL(),
  timeout: 30000, // 30 second timeout for better file upload support
  headers: {
    'Content-Type': 'application/json',
  },
});

// CSRF token management
let csrfToken = null;
let csrfTokenPromise = null;

// Clear CSRF token (useful on logout)
export const clearCSRFToken = () => {
  csrfToken = null;
  csrfTokenPromise = null;
};

export const clearStoredAuthToken = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem('token');
  window.localStorage.removeItem('token');
};

// Fetch CSRF token from backend (requires authentication)
const fetchCSRFToken = async () => {
  try {
    // Use the same api instance so Authorization header is automatically included
    const response = await api.get('/csrf-token', {
      timeout: 5000
    });
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    // If authentication fails, clear any cached token
    if (error.response?.status === 401) {
      csrfToken = null;
    }
    throw error;
  }
};

// Get CSRF token (fetch if not available)
const getCSRFToken = async () => {
  if (csrfToken) {
    return csrfToken;
  }
  
  // If a fetch is already in progress, wait for it
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }
  
  // Start a new fetch
  csrfTokenPromise = fetchCSRFToken().finally(() => {
    csrfTokenPromise = null;
  });
  
  return csrfTokenPromise;
};

// Attach token on init if present
const savedToken = readAuthToken();
if (savedToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Interceptor to inject Authorization header and CSRF token on each request
api.interceptors.request.use(
  async (config) => {
    const token = readAuthToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add CSRF token for state-changing methods
    const stateChangingMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    if (stateChangingMethods.includes(config.method?.toUpperCase())) {
      // Skip CSRF for certain endpoints that don't require it
      const skipCSRFPaths = [
        '/api/auth/login',
        '/api/auth/register',
        '/api/2fa/verify-login',
        '/api/csrf-token'
      ];
      
      const needsCSRF = !skipCSRFPaths.some(path => config.url?.includes(path));
      
      if (needsCSRF) {
        try {
          const token = await getCSRFToken();
          config.headers = config.headers || {};
          config.headers['X-CSRF-Token'] = token;
        } catch (error) {
          // Continue without CSRF token - let the backend handle the error
        }
      }
    }
    
    // For FormData, remove any Content-Type so axios sets multipart/form-data with boundary
    if (config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = { ...config.headers };
      delete headers['Content-Type'];
      delete headers['content-type'];
      config.headers = headers;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Handle CSRF token errors - refresh token and retry once
    if (error.response?.status === 403 && 
        (error.response?.data?.code === 'CSRF_TOKEN_MISSING' || 
         error.response?.data?.code === 'CSRF_TOKEN_INVALID')) {
      // Clear the invalid token
      csrfToken = null;
      
      // Retry the request with a new CSRF token (only once)
      if (error.config && !error.config._csrfRetry) {
        error.config._csrfRetry = true;
        try {
          const newToken = await fetchCSRFToken();
          error.config.headers = error.config.headers || {};
          error.config.headers['X-CSRF-Token'] = newToken;
          return api.request(error.config);
        } catch (retryError) {
          // If it's an auth error, don't retry - user needs to re-authenticate
          if (retryError.response?.status === 401) {
            return Promise.reject(retryError);
          }
        }
      }
    }
    
    // Handle authentication errors - clear CSRF token
    if (error.response?.status === 401) {
      csrfToken = null;
      clearStoredAuthToken();
    }
    
    // Enhanced error handling
    if (error.code === 'ERR_NETWORK') {
      // Network error - check if backend server is running on port 5000
    } else if (error.code === 'ECONNABORTED') {
      // Request timeout - server took too long to respond
    } else if (error.response?.status === 429) {
      // Rate limited - too many requests
    } else if (error.response?.status === 500) {
      console.error('Server error:', error.response?.data?.message || 'Internal server error');
    } else if (error.response?.status === 404) {
      console.error('Not found:', error.config?.url);
    } else {
      console.error('API error:', error.response?.status || error.code || 'Unknown error');
      if (error.response?.data?.message) {
        console.error('Error message:', error.response.data.message);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;